import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Select, TextInput } from "@opendate-app/setlist";

const RULE_TYPE_OPTIONS = [
  { value: "domain", label: "Website domain" },
  { value: "promo_code", label: "Promo code" },
  { value: "trackable_link", label: "Tracking link" },
];

const VALUE_LABELS = { domain: "Domain", promo_code: "Promo code" };

const TITLES = {
  create: "Add partner",
  rename: "Rename partner",
  rules: "Partner rules",
  merge: "Merge partner",
  delete: "Delete partner",
};

const PartnerDialog = ({ dialog, partners, trackableLinkOptions, paths, partnerPath, mutate, onClose }) => {
  const [name, setName] = useState("");
  const [targetId, setTargetId] = useState(null);
  const [ruleType, setRuleType] = useState("domain");
  const [ruleValue, setRuleValue] = useState("");
  const [busy, setBusy] = useState(false);

  const mode = dialog?.mode;
  const partner = dialog?.partner;
  const current = partner ? partners.find((candidate) => candidate.id === partner.id) || partner : null;

  useEffect(() => {
    if (!dialog) return;
    setName(mode === "rename" ? partner.name : "");
    setTargetId(null);
    setRuleType("domain");
    setRuleValue("");
    setBusy(false);
  }, [dialog]);

  const run = async (method, url, body, failureMessage, closeAfter = true) => {
    setBusy(true);
    const { ok } = await mutate(method, url, body, failureMessage);
    setBusy(false);
    if (ok && closeAfter) onClose();
    return ok;
  };

  const submit = () => {
    if (mode === "create") return run("post", paths.partners, { partner: { name } }, "Could not add the partner.");
    if (mode === "rename") return run("patch", partnerPath(partner), { partner: { name } }, "Could not rename the partner.");
    if (mode === "merge") {
      return run("post", paths.merges, { partner_merge: { target_id: targetId, source_id: partner.id } }, "Could not merge the partners.");
    }
    return run("delete", partnerPath(partner), undefined, "Could not delete the partner.");
  };

  const addRule = async () => {
    const ok = await run("post", paths.rules, { partner_rule: { partner_id: partner.id, rule_type: ruleType, value: ruleValue } }, "Could not add the rule.", false);
    if (ok) setRuleValue("");
  };

  const removeRule = (rule) => run("delete", `${paths.rules}/${rule.id}`, undefined, "Could not remove the rule.", false);

  const confirmDisabled = busy || ((mode === "create" || mode === "rename") && name.trim() === "") || (mode === "merge" && !targetId);
  const confirmLabel = { create: "Add partner", rename: "Save", merge: "Merge", delete: "Delete" }[mode];

  const mergeOptions = partners
    .filter((candidate) => partner && candidate.id !== partner.id && !(partner.kind === "artist" && candidate.kind === "artist"))
    .map((candidate) => ({ value: candidate.id, label: candidate.name }));

  const footer =
    mode === "rules" ? (
      <Button onClick={onClose}>Done</Button>
    ) : (
      <>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant={mode === "delete" ? "destructive" : "primary"} onClick={submit} disabled={confirmDisabled}>
          {busy ? "Saving…" : confirmLabel}
        </Button>
      </>
    );

  return (
    <Modal open={Boolean(dialog)} onClose={onClose} icon={false} title={TITLES[mode] || ""} footer={footer}>
      {(mode === "create" || mode === "rename") && (
        <TextInput id="attribution-partner-name" label="Name" value={name} onChange={(event) => setName(event.target.value)} />
      )}

      {mode === "merge" && current && (
        <div>
          <p>{`Move ${current.name}'s rules and attributed visits into another partner, then remove ${current.name}.`}</p>
          <Select id="attribution-merge-target" label="Merge into" options={mergeOptions} value={targetId} onChange={(value) => setTargetId(Number(value))} />
        </div>
      )}

      {mode === "delete" && current && <p>{`Delete ${current.name}? Its rules are removed and its visits go back to their automatic source.`}</p>}

      {mode === "rules" && current && (
        <div>
          {current.rules.length === 0 && <p>No rules yet.</p>}
          {current.rules.map((rule) => (
            <div key={rule.id} className="d-flex align-items-center justify-content-between" style={{ marginBottom: 8 }}>
              <span>{`${RULE_TYPE_OPTIONS.find((option) => option.value === rule.ruleType).label}: ${rule.label}`}</span>
              <Button variant="tertiary" size="sm" disabled={busy} onClick={() => removeRule(rule)} aria-label={`Remove ${rule.label}`}>
                Remove
              </Button>
            </div>
          ))}
          <div style={{ marginTop: 16 }}>
            <Select
              id="attribution-rule-type"
              label="Add a rule"
              options={RULE_TYPE_OPTIONS}
              value={ruleType}
              onChange={(value) => {
                setRuleType(value);
                setRuleValue("");
              }}
            />
          </div>
          <div style={{ marginTop: 12 }}>
            {ruleType === "trackable_link" ? (
              <Select id="attribution-rule-link" label="Tracking link" options={trackableLinkOptions} searchable value={ruleValue || null} onChange={(value) => setRuleValue(String(value))} />
            ) : (
              <TextInput id="attribution-rule-value" label={VALUE_LABELS[ruleType]} value={ruleValue} onChange={(event) => setRuleValue(event.target.value)} />
            )}
          </div>
          <div style={{ marginTop: 12 }}>
            <Button variant="secondary" onClick={addRule} disabled={busy || ruleValue === ""}>
              Add rule
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

PartnerDialog.propTypes = {
  dialog: PropTypes.shape({ mode: PropTypes.string.isRequired, partner: PropTypes.object }),
  partners: PropTypes.array.isRequired,
  trackableLinkOptions: PropTypes.array.isRequired,
  paths: PropTypes.object.isRequired,
  partnerPath: PropTypes.func.isRequired,
  mutate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PartnerDialog;
